import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Editor, EditorState, convertFromRaw } from 'draft-js';
import DOMPurify from 'dompurify';
import 'draft-js/dist/Draft.css';
import { makeStyles, tokens, Button as FluentButton, Link as FluentLink } from '@fluentui/react-components';
import { API_BASE } from '../config.js';
import { iconMap } from '../theme/icons';
import { stonePalette, fititBrandRamp } from '../theme/brand';
import SocialIconFacebook from '../assets/img/components/social-icons/facebook.svg';
import SocialIconInstagram from '../assets/img/components/social-icons/instagram.svg';

const CloseIcon = iconMap.close;
const PhoneIcon = iconMap.phone;

// Šių informacinių puslapių turinys (seedTextPages.js) prasideda <h1>, kuris
// dubliuoja DialogTitle jau rodomą pavadinimą — pašalinama, kad dialoge
// nebūtų dviejų vienodų antraščių (žr. /admin/bugs "Atsidarius dialog langa
// title kartojasi su dialog lango title" — tas pats pataisymas, kaip
// TopBar.jsx, kuris jau naudoja šią funkciją).
const stripDuplicateH1 = (html) => html.replace(/<h1[^>]*>[\s\S]*?<\/h1>/i, '');
const MailIcon = iconMap.mail;

const INFO_LINKS = [
  { to: '/bendradarbiavimas', labelKey: 'footer.info_cooperation' },
  { to: '/taisykles',         labelKey: 'footer.info_terms' },
  { to: '/privatumo-politika', labelKey: 'footer.info_privacy' },
  { to: '/slapuku-politika',  labelKey: 'footer.info_cookies' },
];

const PRIVALUMAI_LINKS = [
  { slug: 'kokybes-garantija',           labelKey: 'footer.adv_quality' },
  { slug: 'pinigu-grazinimas',           labelKey: 'footer.adv_returns' },
  { slug: 'pirkimas-issimoketinai',      labelKey: 'footer.adv_installments' },
  { slug: 'atvyksime-patikslinti-matmenys', labelKey: 'footer.adv_measurements' },
];

// Tamsus fonas (vartotojo prašymu) — tas pats stonePalette[900] "beveik juoda",
// kaip Home.jsx atsiliepimų sekcija. App-lygio Fluent tema ŠVIESI, tad
// tokens.colorNeutralForeground*/colorNeutralStroke* apačioje esantys tekstai
// perrašyti į literalias baltos/rgba reikšmes (tas pats paternas, kaip Home.jsx
// reviewsSection) — semantiniai tokenai skaičiuojami pagal šviesų foną ir čia
// liktų neįskaitomi.
const useStyles = makeStyles({
  footer: {
    backgroundColor: stonePalette[900],
  },
  container: {
    maxWidth: '80rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalXXXL,
    paddingBottom: tokens.spacingVerticalXXXL,
    // Turi sutapti su puslapio turinio konteinerių paddingu (žr. #15).
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  // repeat(auto-fit, minmax(...)) vietoj sluoksniuotų breakpoint'ų — žr. Home.jsx
  // categoriesGrid komentarą: Griffel runtime negarantuoja tvarkos tarp DVIEJŲ tos
  // pačios savybės @media taisyklių, tad 768px/1280px versijos anksčiau lenktyniaudavo
  // atsitiktine tvarka (patikrinta getComputedStyle — 1280px variantas pralaimėdavo).
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    columnGap: tokens.spacingHorizontalXXL,
    rowGap: tokens.spacingHorizontalXXL,
    '@media (min-width: 1280px)': {
      columnGap: tokens.spacingHorizontalXXXL,
      rowGap: tokens.spacingHorizontalXXXL,
    },
  },

  // 1 – Įmonės info
  companyCol: {
    gridColumn: 'span 2 / span 2',
    '@media (min-width: 768px)': { gridColumn: 'span 1 / span 1' },
  },
  companyInfo: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
    lineHeight: '1.6',
    color: 'rgba(255,255,255,0.75)',
    '& p': { margin: 0 },
  },
  companyName: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase300, color: '#ffffff' },
  vatLabel: { fontWeight: tokens.fontWeightRegular, color: 'rgba(255,255,255,0.75)' },
  expositions: { paddingTop: tokens.spacingVerticalXS },

  // 2 / 3 – Informacija / Privalumai
  colHeading: {
    position: 'relative',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginTop: 0,
    marginBottom: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalS,
    color: '#ffffff',
    '::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      bottom: 0,
      width: '20px',
      height: '2px',
      backgroundColor: tokens.colorBrandBackground,
      borderRadius: tokens.borderRadiusCircular,
    },
  },
  linkList: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
    listStyleType: 'none',
    margin: 0,
    padding: 0,
  },
  footerLink: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: '1.6',
    color: 'rgba(255,255,255,0.75)',
    textDecorationLine: 'none',
    transitionProperty: 'color',
    transitionDuration: tokens.durationNormal,
    ':hover': { color: fititBrandRamp[100], textDecorationLine: 'underline' },
  },
  // Fluent <Link as="button"> numatytoji spalva (colorBrandForegroundLink)
  // apskaičiuota ŠVIESIAM fonui, tad ant juodo footer'io perrašoma tuo pačiu
  // paternu, kaip footerLink aukščiau (žr. #18: "Footer padaryk juoda").
  footerLinkButton: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: '1.6',
    textAlign: 'left',
    color: 'rgba(255,255,255,0.75)',
    ':hover': { color: fititBrandRamp[100], textDecorationLine: 'underline' },
  },

  // 4 – Social proof
  socialCol: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalL },
  // Be jokio "box" — vien pats ženkliukas, kaip ir social/kontaktų ikonos ant
  // juodo footer'io fono (žr. /admin/bugs "Google reik nuimti balta background").
  googleReviewBox: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalS,
    width: 'fit-content',
  },
  googleLogo: { width: '2rem', height: '2rem', flexShrink: 0 },
  starsRow: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalXXS, marginBottom: tokens.spacingVerticalXXS },
  star: { width: '14px', height: '14px', fill: '#facc15' },
  googleScoreText: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightBold, color: '#ffffff', margin: 0 },
  googleReviewsText: { fontWeight: tokens.fontWeightRegular, color: 'rgba(255,255,255,0.55)' },
  socialIconsRow: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS },
  // Be jokio "box" — vien pats ikonos ženkliukas (žr. /admin/bugs "Sutvarkyti
  // footer"). Ikonos SVG failai patys yra tamsūs (numatytoji juoda), tad ant
  // juodo footer'io fono paverčiamos baltomis per CSS filter (jos į DOM
  // ateina kaip <img src="*.svg">, todėl currentColor/fill CSS jų nepasiekia).
  socialIconLink: {
    width: '2rem',
    height: '2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.8,
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationNormal,
    ':hover': { opacity: 1 },
  },
  // Padidinta 16px→22px — per maži ir sunkiai atskiriami vienas nuo kito
  // (žr. /admin/bugs "Padidink social icons dydi").
  socialIconImg: { width: '22px', height: '22px', filter: 'brightness(0) invert(1)' },

  // 5 – Kontaktų CTA
  contactList: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalM },
  contactRow: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalMNudge, textDecorationLine: 'none' },
  // Be jokio "box" — vien pats ikonos ženkliukas, kaip ir social ikonos
  // (žr. /admin/bugs "telefono ir el. pašto neturi turėti box, turi būti TIK icons").
  // Sumažinta 2rem→1.25rem — dėžutė ženkliuko centravimui buvo daug platesnė
  // už patį 16px ikoną, todėl tarpas iki teksto dešinėje atrodė per didelis
  // (žr. /admin/bugs "sumazink atstuma tarp telefono ir mail icons ir teksto").
  contactIconChip: {
    width: '1.25rem',
    height: '1.25rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: '#ffffff',
    fontSize: '18px',
    opacity: 0.9,
    transitionProperty: 'opacity',
    transitionDuration: tokens.durationNormal,
  },
  contactPhoneText: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: 'rgba(255,255,255,0.75)' },
  contactEmailText: { fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightSemibold, color: 'rgba(255,255,255,0.75)' },

  // Apatinė juosta
  bottomBar: {
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    borderTop: '1px solid rgba(255,255,255,0.12)',
  },
  bottomBarInner: {
    maxWidth: '80rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    rowGap: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase200,
    color: 'rgba(255,255,255,0.55)',
    // Turi sutapti su puslapio turinio konteinerių paddingu (žr. #15).
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
    '@media (min-width: 640px)': { flexDirection: 'row' },
  },
  bottomLinksRow: { display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalL },
  bottomLink: {
    color: 'rgba(255,255,255,0.55)',
    textDecorationLine: 'none',
    transitionProperty: 'color',
    transitionDuration: tokens.durationNormal,
    ':hover': { color: fititBrandRamp[100] },
  },

  // Informacinis modalas (Privalumai turinys)
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingHorizontalM,
  },
  modalBackdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
  },
  modalPanel: {
    position: 'relative',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadius2XLarge,
    boxShadow: tokens.shadow64,
    width: '100%',
    maxWidth: '32rem',
    animationDuration: '0.2s',
    animationTimingFunction: 'ease-out',
    animationName: {
      from: { opacity: 0, transform: 'scale(0.96)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  modalTitle: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, margin: 0 },
  modalCloseBtn: {
    width: '2.75rem',
    height: '2.75rem',
    minWidth: 'unset',
    padding: 0,
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transitionProperty: 'background-color',
    transitionDuration: tokens.durationNormal,
    ':hover': { backgroundColor: tokens.colorNeutralBackground4 },
  },
  modalBody: {
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    paddingTop: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.6',
    overflowY: 'auto',
    maxHeight: '70vh',
  },
  modalBodyHtml: {
    '& p': { marginBottom: tokens.spacingVerticalS },
    '& p:last-child': { marginBottom: 0 },
    '& ul': { listStyleType: 'disc', marginLeft: '1.25rem', marginBottom: tokens.spacingVerticalS },
    '& ol': { listStyleType: 'decimal', marginLeft: '1.25rem', marginBottom: tokens.spacingVerticalS },
    '& li': { marginBottom: tokens.spacingVerticalXXS },
    '& strong': { fontWeight: tokens.fontWeightSemibold },
    '& a': { color: tokens.colorBrandForeground2, textDecorationLine: 'underline', textUnderlineOffset: '2px' },
  },
});

function StarIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
  );
}

function Footer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const s = useStyles();
  const [open, setOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [editorState, setEditorState] = useState(null);
  const [plainHtml, setPlainHtml] = useState('');

  const fetchContent = async (slug, fallbackLabel) => {
    try {
      const res = await fetch(`${API_BASE}/textpages/${slug}`);
      if (!res.ok) return;
      const data = await res.json();
      setDialogTitle(data.title || fallbackLabel);
      try {
        const parsed = JSON.parse(data.content);
        if (parsed && parsed.blocks) {
          setEditorState(EditorState.createWithContent(convertFromRaw(parsed)));
          setPlainHtml('');
        } else {
          setEditorState(null);
          setPlainHtml(data.content || '');
        }
      } catch {
        setEditorState(null);
        setPlainHtml(data.content || '');
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpen = (slug, label) => { fetchContent(slug, label); setOpen(true); };
  const handleClose = () => { setOpen(false); setEditorState(null); setPlainHtml(''); setDialogTitle(''); };

  if (pathname.startsWith('/konfiguratorius')) return null;
  return (
    <>
    <footer className={s.footer}>
      <div className={s.container}>
        <div className={s.grid}>

          {/* 1 – Company info */}
          <div className={s.companyCol}>
            <div className={s.companyInfo}>
              <p className={s.companyName}>{t('footer.company_name')}</p>
              <p>{t('footer.company_code_label')} {t('footer.company_code')}</p>
              <p><span className={s.vatLabel}>{t('footer.vat_label')}</span> {t('footer.vat')}</p>
              <p>{t('footer.address')}</p>
              <p className={s.expositions}>{t('footer.expositions')}</p>
            </div>
          </div>

          {/* 2 – Informacija */}
          <div>
            <h6 className={s.colHeading}>{t('footer.section_info')}</h6>
            <ul className={s.linkList}>
              {INFO_LINKS.map(({ to, labelKey }) => (
                <li key={to}>
                  <Link to={to} className={s.footerLink}>{t(labelKey)}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* 3 – Privalumai */}
          <div>
            <h6 className={s.colHeading}>{t('footer.section_advantages')}</h6>
            <ul className={s.linkList}>
              {PRIVALUMAI_LINKS.map(({ slug, labelKey }) => (
                <li key={slug}>
                  <FluentLink as="button" onClick={() => handleOpen(slug, t(labelKey))} className={s.footerLinkButton}>
                    {t(labelKey)}
                  </FluentLink>
                </li>
              ))}
            </ul>
          </div>

          {/* 4 – Social proof */}
          <div className={s.socialCol}>
            {/* Google Reviews */}
            <div className={s.googleReviewBox}>
              <svg viewBox="0 0 48 48" className={s.googleLogo}>
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                <path fill="none" d="M0 0h48v48H0z"/>
              </svg>
              <div>
                <div className={s.starsRow}>
                  {[1,2,3,4,5].map(i => <StarIcon key={i} className={s.star} />)}
                </div>
                <p className={s.googleScoreText}>{t('footer.google_score')} <span className={s.googleReviewsText}>· {t('footer.google_reviews')}</span></p>
              </div>
            </div>

            {/* Social icons */}
            <div className={s.socialIconsRow}>
              {[
                { src: SocialIconFacebook, alt: 'Facebook', href: 'https://www.facebook.com/fitit.lt' },
                { src: SocialIconInstagram, alt: 'Instagram', href: 'https://www.instagram.com/fitit.lt' },
              ].map(({ src, alt, href }) => (
                <a key={alt} href={href} target="_blank" rel="noopener noreferrer" className={s.socialIconLink}>
                  <img src={src} alt={alt} className={s.socialIconImg} />
                </a>
              ))}
            </div>
          </div>

          {/* 5 – Contact CTA */}
          <div>
            <h6 className={s.colHeading}>{t('footer.section_contact')}</h6>
            <div className={s.contactList}>
              <a href="tel:+37060517874" className={s.contactRow}>
                <div className={s.contactIconChip}>
                  {PhoneIcon && <PhoneIcon fontSize={16} />}
                </div>
                <span className={s.contactPhoneText}>{t('footer.phone')}</span>
              </a>
              <a href="mailto:info@fitit.lt" className={s.contactRow}>
                <div className={s.contactIconChip}>
                  {MailIcon && <MailIcon fontSize={16} />}
                </div>
                <span className={s.contactEmailText}>{t('footer.email')}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className={s.bottomBar}>
        <div className={s.bottomBarInner}>
          <span>© {new Date().getFullYear()} {t('footer.company_name')}. {t('footer.copyright')}</span>
          <div className={s.bottomLinksRow}>
            <Link to="/privatumo-politika" className={s.bottomLink}>{t('footer.info_privacy')}</Link>
            <Link to="/slapuku-politika" className={s.bottomLink}>{t('footer.info_cookies')}</Link>
          </div>
        </div>
      </div>
    </footer>

      {open && (
        <div className={s.modalOverlay}>
          <div className={s.modalBackdrop} onClick={handleClose} />
          <div className={s.modalPanel}>
            <div className={s.modalHeader}>
              <h3 className={s.modalTitle}>{dialogTitle || 'Informacija'}</h3>
              <FluentButton
                appearance="subtle"
                className={s.modalCloseBtn}
                onClick={handleClose}
                icon={CloseIcon ? <CloseIcon fontSize={18} /> : undefined}
              />
            </div>
            <div className={s.modalBody}>
              {editorState ? (
                <Editor editorState={editorState} readOnly onChange={() => {}} />
              ) : (
                <div
                  className={s.modalBodyHtml}
                  dangerouslySetInnerHTML={{ __html: stripDuplicateH1(DOMPurify.sanitize(plainHtml)) }}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Footer;
